'use client';

import { useState, useEffect } from 'react';
import { Session } from 'next-auth';
import Link from 'next/link';
import { CheckCircle, Clock, DollarSign } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
interface NosAgentsDashboardClientProps {
    session: Session;
}

interface PersonaBid {
    id: string;
    prompt: string;
    reason: string;
    relevance?: number;
    examples: string[];
    rolePrompt: string;
    companyCount?: number;
    bidAmount: number;
    status: string;
    businessAnalysis?: string;
    inputValue?: string;
    createdAt: string;
}

export default function NosAgentsDashboardClient({ session }: NosAgentsDashboardClientProps) {
    const [personaBids, setPersonaBids] = useState<PersonaBid[]>([]);
    const [loading, setLoading] = useState(true);
    const [hasNoDomains, setHasNoDomains] = useState(false);
    const [domainsLoading, setDomainsLoading] = useState(true);

    useEffect(() => {
        fetchPersonaBids();
        checkDomains();
    }, []);

    const checkDomains = async () => {
        try {
            if (!session?.siteId) {
                console.error('No site ID found in session');
                return;
            }

            const response = await fetch(`/api/sites/${session.siteId}/domains`);
            if (response.ok) {
                const data = await response.json();
                const totalDomains = (data.enabledDomains?.length || 0) + (data.disabledDomains?.length || 0);
                setHasNoDomains(totalDomains === 0);
            }
        } catch (error) {
            console.error('Error checking domains:', error);
        } finally {
            setDomainsLoading(false);
        }
    };

    const fetchPersonaBids = async () => {
        try {
            if (!session?.siteId) {
                console.error('No site ID found in session');
                return;
            }

            const response = await fetch(`/api/sites/${session.siteId}/persona-bids`);
            if (response.ok) {
                const data = await response.json();
                setPersonaBids(data.bids || []);
            }
        } catch (error) {
            console.error('Error fetching persona bids:', error);
        } finally {
            setLoading(false);
        }
    };

    const getTotalBidValue = () => {
        return personaBids.reduce((sum: number, bid: PersonaBid) => sum + bid.bidAmount, 0);
    };
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white">
            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Domain Check Banner */}
                {!domainsLoading && hasNoDomains && (
                    <Card className="mb-6 border-orange-200 bg-orange-50">
                        <CardContent className="p-6 text-center">
                            <h3 className="text-lg font-semibold text-orange-900 mb-2">
                                Ready to activate your AI agents?
                            </h3>
                            <p className="text-orange-700 mb-4">
                                Get started by buying a branded domain (~$10).
                            </p>
                            <Button
                                size="lg"
                                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white"
                                asChild
                            >
                                <Link href="/domains">
                                    <DollarSign className="w-4 h-4 mr-2" />
                                    Buy Domain (~$10)
                                </Link>
                            </Button>
                        </CardContent>
                    </Card>
                )}

                <div className="mb-8">
                    <h1 className="text-4xl font-bold text-gray-900 mb-4">
                        Welcome to NOS Agents Dashboard
                    </h1>
                    <p className="text-lg text-gray-600">
                        Manage your AI agents, domains, and bookings all in one place.
                    </p>
                </div>

                {loading ? (
                    <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto"></div>
                        <p className="text-gray-600 mt-2">Loading your persona bids...</p>
                    </div>
                ) : personaBids.length > 0 ? (
                    <div className="space-y-6">
                        {/* Summary Card */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                    Your Agent Bids Summary
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-teal-600">{personaBids.length}</div>
                                        <div className="text-sm text-gray-600">Total Personas</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-green-600">${getTotalBidValue()}</div>
                                        <div className="text-sm text-gray-600">Total Bid Value</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-blue-600">${Math.round(getTotalBidValue() / personaBids.length)}</div>
                                        <div className="text-sm text-gray-600">Avg. per Persona</div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Persona Bids Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {personaBids.map((bid) => (
                                <Card key={bid.id} className="hover:shadow-lg transition-shadow">
                                    <CardContent className="p-6">
                                        <div className="mb-4">
                                            <div className="flex items-center justify-between mb-2">
                                                <Badge variant={bid.status === 'PENDING' ? 'secondary' : 'default'}>
                                                    {bid.status}
                                                </Badge>
                                                {bid.relevance && (
                                                    <Badge variant="outline">
                                                        {Math.round(bid.relevance * 100)}% Match
                                                    </Badge>
                                                )}
                                            </div>
                                            <h3 className="font-semibold text-gray-900 text-lg">
                                                {bid.rolePrompt} at {bid.prompt}
                                            </h3>
                                        </div>

                                        <p className="text-sm text-gray-600 mb-4 line-clamp-3">{bid.reason}</p>

                                        {bid.companyCount && (
                                            <div className="text-xs text-gray-500 mb-3">
                                                ~{bid.companyCount.toLocaleString()} companies
                                            </div>
                                        )}

                                        <div className="border-t pt-3">
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm font-medium text-gray-700">Bid Amount:</span>
                                                <span className="text-lg font-bold text-green-600">
                                                    ${bid.bidAmount}
                                                </span>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>

                        {/* Call to Action - Only show if user has domains */}
                        {!hasNoDomains && (
                            <Card>
                                <CardContent className="p-6 text-center">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                        Ready to activate your AI agents?
                                    </h3>
                                    <p className="text-gray-600 mb-4">
                                        Your persona bids are ready to be sent to our agent network. Start getting qualified meetings today!
                                    </p>
                                    <Button size="lg" className="bg-gradient-to-r from-teal-500 to-green-500 hover:from-teal-600 hover:to-green-600 text-white">
                                        <DollarSign className="w-4 h-4 mr-2" />
                                        Activate Agents (${getTotalBidValue()})
                                    </Button>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                ) : (
                    <div className="text-center py-12">
                        <div className="bg-white rounded-lg shadow-md p-8 max-w-2xl mx-auto">
                            <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                            <h2 className="text-2xl font-semibold text-gray-800 mb-4">No Agent Bids Yet</h2>
                            <p className="text-gray-600 mb-6">
                                You haven't created any persona bids yet. Visit our landing page to analyze your business and create your first agent bids.
                            </p>
                            <Button asChild>
                                <Link href="/nosagents">Create Your First Bid</Link>
                            </Button>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}
